import { useRef, useState } from 'react'
import {
  ArrowUp,
  X,
  CalendarClock,
  Mail,
  Phone,
  AlertTriangle,
  Clock,
  ChevronDown,
  GitBranch,
  ClipboardList,
} from 'lucide-react'
import { Button } from '../../lib/shadcn/button'
import { Sheet, SheetClose, SheetContent, SheetTitle } from '../../lib/shadcn/sheet'
import { InterviewScoringPanel } from './InterviewScoringPanel'
import { ProgressStrip } from './ProgressStrip'
import { Chip } from './Chip'
import { DecisionActions, type OutcomeAction } from './DecisionActions'
import { DuplicateDialog } from './DuplicateDialog'
import { StageControl } from './StageControl'
import { ScreeningChips } from './ScreeningChips'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../../lib/shadcn/collapsible'
import {
  type WorklistRow,
  roundLabel,
  statusLabel,
  statusTone,
  formatDateTime,
  dueFlag,
  initials,
  matchBasisLabel,
} from '../../utils/interviews'

export function WorklistCard({
  item,
  canAct,
  busy,
  onSubmit,
  onChanged,
  isDuplicate = false,
}: {
  item: WorklistRow
  canAct: boolean
  busy: boolean
  onSubmit: (interviewId: string, action: OutcomeAction, notes: string | null) => Promise<void>
  onChanged: () => void
  isDuplicate?: boolean
}) {
  const flag = dueFlag(item.interview_at)
  const name = item.full_name || 'Unnamed applicant'
  const [stageOpen, setStageOpen] = useState(false)
  const [scoringOpen, setScoringOpen] = useState(false)
  const sheetBodyRef = useRef<HTMLDivElement>(null)

  return (
    <div className="rounded-lg border border-border bg-card p-4 shadow-retool-sm">
      {isDuplicate && item.duplicate_survivor_name && (
        <div className="mb-3 flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-2.5 text-xs text-amber-900 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-200">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span>
            Already further along: {item.duplicate_survivor_name}
            {matchBasisLabel(item.duplicate_match_basis)
              ? ` (${matchBasisLabel(item.duplicate_match_basis)})`
              : ''}
          </span>
        </div>
      )}

      <div className="flex items-start gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[hsl(var(--brand-soft))] text-sm font-semibold text-[hsl(var(--brand-soft-foreground))]">
          {initials(item.full_name)}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h3 className="truncate text-base font-semibold text-foreground">{name}</h3>
            {item.score != null && (
              <span className="shrink-0 text-xs font-semibold tabular-nums text-muted-foreground">
                Score {item.score}
              </span>
            )}
          </div>
          <p className="mt-0.5 text-sm text-muted-foreground">{roundLabel(item.round)}</p>

          <div className="mt-1.5">
            <Chip tone={statusTone(item.current_status)}>{statusLabel(item.current_status)}</Chip>
          </div>

          <div className="mt-1.5">
            <ScreeningChips app={item} compact />
          </div>

          <div className="mt-2 flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center gap-1.5 text-sm text-foreground">
              <CalendarClock className="h-4 w-4 text-muted-foreground" />
              {formatDateTime(item.interview_at)}
            </span>
            {flag === 'today' && (
              <Chip tone="brand" icon={<Clock className="h-3 w-3" />}>
                Today
              </Chip>
            )}
            {flag === 'overdue' && (
              <Chip tone="red" icon={<AlertTriangle className="h-3 w-3" />}>
                Overdue
              </Chip>
            )}
          </div>

          <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
            <span className="inline-flex items-center gap-1.5">
              <Mail className="h-3.5 w-3.5" />
              {item.primary_email || '—'}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <Phone className="h-3.5 w-3.5" />
              {item.primary_phone || '—'}
            </span>
          </div>
        </div>
      </div>

      {/* The script, the questions and the scorecard for THIS interview, opened
          from Today so the interviewer never has to go and find the applicant.
          Migration 0265: the score records the outcome, so this is the main
          action on the card. Who may score is decided in the database. */}
      {/* Who still has to score and where the references stand. Everybody who can
          see the card sees this, including Yolisa and Xolile, who cannot score. */}
      {item.progress && (
        <div className="mt-3">
          <ProgressStrip progress={item.progress} />
        </div>
      )}

      <div className="mt-3">
        <Button variant="brand" className="h-11 w-full sm:h-9 sm:w-auto" onClick={() => setScoringOpen(true)}>
          <ClipboardList className="h-4 w-4" />
          Open script and scoring
        </Button>
      </div>

      <Sheet open={scoringOpen} onOpenChange={setScoringOpen}>
        {/* The sheet itself does not scroll; only the body does. The script runs to
            dozens of questions, and when the whole sheet scrolled, the close
            button in its corner scrolled away with it and a phone had no way out
            but the back gesture. The header, the corner close and the floating
            buttons now stay where they are. */}
        <SheetContent side="right" className="flex h-full w-full flex-col gap-0 p-0 sm:max-w-3xl">
          <div className="shrink-0 space-y-2 border-b border-border px-4 pb-3 pt-4 sm:px-6">
            <SheetTitle className="pr-10 text-base sm:text-lg">
              {name}: {roundLabel(item.round)}
            </SheetTitle>
            <p className="text-xs text-muted-foreground">
              {formatDateTime(item.interview_at)}. Read the script in grey as it is written, ask each question, and
              tap the answer that matches what he said.
            </p>
            <ProgressStrip progress={item.progress} />
          </div>
          <div ref={sheetBodyRef} className="min-h-0 flex-1 overflow-y-auto px-4 pb-24 pt-4 sm:px-6">
            {scoringOpen && (
              <InterviewScoringPanel
                applicationId={item.application_id}
                focusInterviewId={item.interview_id}
                onOutcomeRecorded={onChanged}
              />
            )}
          </div>
          <div className="pointer-events-none absolute inset-x-0 bottom-0 flex justify-end gap-2 p-4">
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="pointer-events-auto h-11 rounded-full bg-card px-4 shadow-retool-lg"
              onClick={() => sheetBodyRef.current?.scrollTo({ top: 0, behavior: 'smooth' })}
              aria-label="Back to the top of the script"
            >
              <ArrowUp className="h-4 w-4" />
              Top
            </Button>
            <SheetClose asChild>
              <Button
                type="button"
                variant="brand"
                size="sm"
                className="pointer-events-auto h-11 rounded-full px-5 shadow-retool-lg"
              >
                <X className="h-4 w-4" />
                Close
              </Button>
            </SheetClose>
          </div>
        </SheetContent>
      </Sheet>

      {canAct && (
        <div className="mt-4 space-y-3 border-t border-border pt-3">
          <DecisionActions
            interviewId={item.interview_id}
            applicantName={name}
            round={item.round}
            canAct={canAct}
            busy={busy}
            onSubmit={onSubmit}
          />

          <DuplicateDialog
            applicationId={item.application_id}
            applicantName={name}
            highlightSurvivorId={item.duplicate_survivor_application_id ?? null}
            onChanged={onChanged}
          />

          <Collapsible open={stageOpen} onOpenChange={setStageOpen}>
            <CollapsibleTrigger className="flex w-full items-center justify-between rounded-md border border-border bg-muted/30 px-3 py-2 text-sm font-medium text-foreground transition-colors hover:bg-muted">
              <span className="inline-flex items-center gap-2">
                <GitBranch className="h-4 w-4 text-[hsl(var(--brand))]" />
                Adjust pipeline stage
              </span>
              <ChevronDown
                className={
                  'h-4 w-4 text-muted-foreground transition-transform ' +
                  (stageOpen ? 'rotate-180' : '')
                }
              />
            </CollapsibleTrigger>
            <CollapsibleContent className="pt-2">
              <StageControl
                applicationId={item.application_id}
                currentStatus={item.current_status}
                canAct={canAct}
                onSaved={onChanged}
              />
            </CollapsibleContent>
          </Collapsible>
        </div>
      )}
    </div>
  )
}
